<script setup>
import { useHelpCenter } from '@/composables/useHelpCenter'

// pages/help/index.vue — Module 24 (§9.10). Hub Help Center — grup+item
// DIAMBIL DARI API (bukan hardcode seperti pola PintuKasir), supaya
// permission-gating konsisten dengan backend (HelpCenterService), bukan
// cuma disembunyikan di frontend.
definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'HelpCenter',
  },
})

const { groups, loading, fetchGroups } = useHelpCenter()

onMounted(fetchGroups)

const searchQuery = ref('')

const filteredGroups = computed(() => {
  if (!searchQuery.value) return groups.value

  const query = searchQuery.value.toLowerCase()

  return groups.value
    .map(group => ({
      ...group,
      items: group.items.filter(item =>
        item.title.toLowerCase().includes(query) || item.description.toLowerCase().includes(query),
      ),
    }))
    .filter(group => group.items.length > 0)
})

const totalItems = computed(() => groups.value.reduce((acc, g) => acc + g.items.length, 0))
</script>

<template>
  <div>
    <VRow>
      <VCol cols="12">
        <h4 class="text-h4 font-weight-bold mb-2">
          Bantuan &amp; Panduan Penggunaan
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-6">
          Panduan lengkap penggunaan QUALIVA — apa yang tampil di sini menyesuaikan hak akses Anda.
        </p>
      </VCol>
    </VRow>

    <VRow class="mb-4">
      <VCol cols="12">
        <RouterLink
          :to="{ name: 'help-onboarding' }"
          class="text-decoration-none"
        >
          <VCard
            color="primary"
            variant="tonal"
            hover
          >
            <VCardText class="d-flex align-center gap-4">
              <VAvatar
                color="primary"
                size="56"
                rounded="lg"
              >
                <VIcon
                  icon="ri-flag-line"
                  size="32"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 font-weight-bold mb-1">
                  Mulai dari Sini: Onboarding &amp; Urutan Setup Awal
                </h6>
                <p class="text-body-2 mb-0">
                  Checklist interaktif urutan wajib pengisian data — sangat disarankan untuk instalasi baru.
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>

    <VRow>
      <VCol
        cols="12"
        md="6"
      >
        <VTextField
          v-model="searchQuery"
          placeholder="Cari panduan (mis. dokumen, checklist, backup...)"
          prepend-inner-icon="ri-search-line"
          clearable
          hide-details
        />
      </VCol>
    </VRow>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="my-4"
    />

    <VRow class="mt-4">
      <VCol
        v-for="group in filteredGroups"
        :key="group.slug"
        cols="12"
        md="6"
        lg="4"
        class="mb-2"
      >
        <VCard
          variant="outlined"
          class="h-100"
        >
          <VCardText>
            <div class="d-flex align-center mb-4">
              <VAvatar
                :color="group.color"
                variant="tonal"
                size="48"
                class="me-3"
              >
                <VIcon
                  :icon="group.icon"
                  size="28"
                />
              </VAvatar>
              <div>
                <h6 class="text-h6 font-weight-bold mb-0">
                  {{ group.title }}
                </h6>
                <div class="text-caption text-disabled">
                  {{ group.items.length }} panduan
                </div>
              </div>
            </div>

            <VDivider class="mb-3" />

            <VList density="compact">
              <VListItem
                v-for="item in group.items"
                :key="item.slug"
                :to="{ name: item.route }"
                rounded="lg"
              >
                <VListItemTitle class="text-body-2 font-weight-medium">
                  {{ item.title }}
                </VListItemTitle>
                <VListItemSubtitle class="text-caption mt-1">
                  {{ item.description }}
                </VListItemSubtitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        v-if="!loading && !filteredGroups.length"
        cols="12"
      >
        <p class="text-medium-emphasis text-center py-10">
          Tidak ada panduan yang cocok dengan pencarian Anda.
        </p>
      </VCol>
    </VRow>

    <VRow
      v-if="!loading"
      class="mt-2"
    >
      <VCol cols="12">
        <VCard
          color="primary"
          variant="tonal"
        >
          <VCardText class="d-flex align-center justify-space-between flex-wrap gap-4">
            <div>
              <h6 class="text-h6 mb-1">
                Total Panduan
              </h6>
              <p class="text-body-2 mb-0">
                {{ totalItems }} topik tersedia untuk peran Anda
              </p>
            </div>
            <VChip
              color="primary"
              size="large"
            >
              {{ groups.length }} Kategori
            </VChip>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
